import swal from 'sweetalert';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import axiosInstance from './axios_signed';
import { ResponseT } from 'stores/interfaces';
import { logout } from 'stores/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { authMenu } from 'menu';
import { ErrorResponseEnum } from 'enums';
import { RootState } from 'stores/store';
import { selectCompanyId, selectProjectId } from 'stores/projectCompanySlice';
import { mergeUrlParams } from './helpers';

export enum MethodTypeEnum {
	Put,
	Patch,
	Post,
}

const useAuthService = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const companyId = useSelector((state: RootState) => selectCompanyId(state));
	const projectId = useSelector((state: RootState) => selectProjectId(state));

	async function getRequest<T>(link: string, load = true): Promise<ResponseT<T>> {
		try {
			if (load) NProgress.start();
			const url = mergeUrlParams(link, { company_id: companyId, project_id: projectId });

			const response = await axiosInstance.get<ResponseT<T>>(url);
			if (load) NProgress.done();
			return response.data;
		} catch (error: any) {
			if (load) NProgress.done();
			if (error.response?.status === 401) {
				dispatch(logout());
				navigate(authMenu.login.path, { replace: true });
			}
			const errorMessage =
				error?.response?.data?.message || error.message || ErrorResponseEnum.ERROR_OCCURRED;
			const sanitizedMessage = String(errorMessage).replace(/<[^>]*>/g, '');
			swal(ErrorResponseEnum.REQUEST_FAILED, sanitizedMessage, 'error');
			throw new Error(sanitizedMessage);
		}
	}

	async function deleteRequest<T>(link: string, load = true): Promise<ResponseT<T>> {
		try {
			if (load) NProgress.start();
			const url = mergeUrlParams(link, { company_id: companyId, project_id: projectId });

			const response = await axiosInstance.delete<ResponseT<T>>(url);
			if (load) NProgress.done();
			return response.data;
		} catch (error: any) {
			if (load) NProgress.done();
			if (error.response?.status === 401) {
				dispatch(logout());
				navigate(authMenu.login.path, { replace: true });
			}
			const errorMessage =
				error?.response?.data?.message || error.message || ErrorResponseEnum.ERROR_OCCURRED;
			const sanitizedMessage = String(errorMessage).replace(/<[^>]*>/g, '');
			swal(ErrorResponseEnum.REQUEST_FAILED, sanitizedMessage, 'error');
			throw new Error(sanitizedMessage);
		}
	}

	async function bodyRequest<T>(
		link: string,
		load = true,
		params: any,
		type: MethodTypeEnum,
	): Promise<ResponseT<T>> {
		try {
			if (load) NProgress.start();
			const url = mergeUrlParams(link, { company_id: companyId, project_id: projectId });
			let response;
			switch (type) {
				case MethodTypeEnum.Patch:
					response = await axiosInstance.patch<ResponseT<T>>(url, params);
					break;
				case MethodTypeEnum.Put:
					response = await axiosInstance.put<ResponseT<T>>(url, params);
					break;
				default:
					response = await axiosInstance.post<ResponseT<T>>(url, params);
					break;
			}
			if (load) NProgress.done();
			return response.data;
		} catch (error: any) {
			if (load) NProgress.done();
			if (error.response?.status === 401) {
				dispatch(logout());
				navigate(authMenu.login.path, { replace: true });
			}
			const errorMessage =
				error?.response?.data?.message || error.message || ErrorResponseEnum.ERROR_OCCURRED;
			const sanitizedMessage = String(errorMessage).replace(/<[^>]*>/g, '');
			swal(ErrorResponseEnum.REQUEST_FAILED, sanitizedMessage, 'error');
			throw new Error(sanitizedMessage);
		}
	}

	return { getRequest, deleteRequest, bodyRequest };
};

export default useAuthService;
